import { Fragment, useState } from 'react'
import { ActionIcon, Alert, Badge, Button, Loader, Stack, Table, Text } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import type { OperacaoDoGateway } from '@/api/whatsappGateways'
import { formatDuration } from '@/components/admin/tenants/jobs/stepMeta'
import { useOperacoes, usePassosDoJob } from '@/hooks/useWhatsappGateways'
import { LinhaDoTempoDoJob } from './LinhaDoTempoDoJob'
import { mensagemDeErro } from './mensagens'
import { ROTULO_KIND, ROTULO_STATUS_DO_JOB, fraseDoErro } from './passosDoJob'

const COR_STATUS: Record<OperacaoDoGateway['status'], string> = {
  pending: 'gray', running: 'blue', success: 'green', failed: 'red',
}

function PassosDoJob({ orgId, gwId, op }: { orgId: number; gwId: string; op: OperacaoDoGateway }) {
  const passos = usePassosDoJob(orgId, gwId, op.id, op.status === 'pending' || op.status === 'running')
  if (passos.isLoading) return <Loader size="sm" />
  if (passos.isError) return <Alert color="red" variant="light">{mensagemDeErro(passos.error)}</Alert>
  if (!passos.data) return null
  if (!passos.data.passos?.length) {
    return (
      <Text size="sm" c="dimmed">
        {op.status === 'pending' ? 'O job ainda não começou: nenhum passo rodou.' : 'Job anterior à linha do tempo dos passos: só a situação e o erro foram gravados.'}
      </Text>
    )
  }
  return <LinhaDoTempoDoJob job={passos.data} titulo={null} />
}

/**
 * Aba "Operações" do detalhe: todo job do gateway (provisionar, atualizar, remover),
 * mais recente primeiro, com quem pediu, quando, quanto durou e o erro. A linha abre a
 * linha do tempo daquele job, lida na hora pela rota de passos.
 */
export function OperacoesDoGateway({ orgId, gwId }: { orgId: number; gwId: string }) {
  const ops = useOperacoes(orgId, gwId)
  const [abertos, setAbertos] = useState<ReadonlySet<number>>(new Set())
  const linhas = ops.data?.pages.flatMap((p) => p.items) ?? []

  function alternar(id: number) {
    setAbertos((atual) => {
      const novo = new Set(atual)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })
  }

  if (ops.isLoading) return <Loader size="sm" />
  if (ops.isError) return <Alert color="red" variant="light">{mensagemDeErro(ops.error)}</Alert>
  if (linhas.length === 0) return <Text size="sm" c="dimmed">Nenhuma operação registrada.</Text>

  return (
    <Stack gap="xs">
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={36} />
            <Table.Th>Operação</Table.Th>
            <Table.Th>Situação</Table.Th>
            <Table.Th>Quem pediu</Table.Th>
            <Table.Th>Quando</Table.Th>
            <Table.Th>Duração</Table.Th>
            <Table.Th>Erro</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {linhas.map((op) => {
            const aberto = abertos.has(op.id)
            return (
              <Fragment key={op.id}>
                <Table.Tr data-testid={`operacao-${op.id}`} style={{ cursor: 'pointer' }} onClick={() => alternar(op.id)}>
                  <Table.Td>
                    <ActionIcon variant="subtle" size="sm" aria-expanded={aberto}
                      aria-label={aberto ? 'Esconder os passos' : 'Ver os passos'}
                      onClick={(e) => { e.stopPropagation(); alternar(op.id) }}>
                      {aberto ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td>{ROTULO_KIND[op.kind]}</Table.Td>
                  <Table.Td><Badge variant="light" color={COR_STATUS[op.status]}>{ROTULO_STATUS_DO_JOB[op.status]}</Badge></Table.Td>
                  <Table.Td><Text size="sm">{op.requested_by_nome ?? 'Sistema (automático)'}</Text></Table.Td>
                  <Table.Td><Text ff="monospace" size="xs">{new Date(op.created_at).toLocaleString('pt-BR')}</Text></Table.Td>
                  <Table.Td><Text size="sm">{op.duracao_s != null ? formatDuration(op.duracao_s) : '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{op.status === 'failed' ? (fraseDoErro(op.erro) ?? 'Falhou sem código gravado') : '—'}</Text></Table.Td>
                </Table.Tr>
                {aberto && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <PassosDoJob orgId={orgId} gwId={gwId} op={op} />
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            )
          })}
        </Table.Tbody>
      </Table>
      {ops.hasNextPage && (
        <Button variant="subtle" size="xs" loading={ops.isFetchingNextPage} onClick={() => void ops.fetchNextPage()}>
          Carregar mais
        </Button>
      )}
    </Stack>
  )
}
